import { useState, useCallback, useMemo, useEffect } from 'react'
import { Plus, Phone } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/data/DataTable'
import { ContactFormDialog } from '../components/ContactFormDialog'
import { ContactDeleteDialog } from '../components/ContactDeleteDialog'
import { ContactViewDialog } from '../components/ContactViewDialog'
import { useContacts, useDeleteContact } from '../hooks'
import { createContactColumns } from '../columns'
import { debounce } from '@/lib/utils'
import { useCan } from '@/hooks/useCan'
import type { Contact } from '@/types/entities'
import type { ListParams } from '@/lib/api/types'

export function ContactListPage() {
    const { t } = useTranslation()
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
    const [showCreateDialog, setShowCreateDialog] = useState(false)
    const [showEditDialog, setShowEditDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [showViewDialog, setShowViewDialog] = useState(false)
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(10)
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    // The screen is open to every contacts.read; a change is offered only where the API allows it.
    const canCreate = useCan('contacts.create')
    const canUpdate = useCan('contacts.update')
    const canDelete = useCan('contacts.delete')

    // Debounce search so we don't refetch on every keystroke
    const debouncedSetSearch = useMemo(() => debounce((value: string) => {
        setDebouncedSearch(value)
        setPage(1) // Reset to page 1 when search changes
    }, 350), [])
    // A search still waiting when the page closes is dropped, not applied to a page that is gone.
    useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch])

    useEffect(() => {
        debouncedSetSearch(search)
    }, [search, debouncedSetSearch])

    // Memoize params to prevent unnecessary re-renders
    const params: ListParams = useMemo(() => ({
        page,
        perPage: limit,
        search: debouncedSearch || undefined
    }), [page, limit, debouncedSearch])

    const { data: contactsResponse, isLoading } = useContacts(params)
    const deleteContact = useDeleteContact()

    // Memoize data extraction to prevent unnecessary recalculations
    const apiResponse = useMemo(() => contactsResponse?.data, [contactsResponse])
    const contacts = useMemo(() => apiResponse?.data || [], [apiResponse])
    const totalContacts = useMemo(() => 
        apiResponse?.pagination?.total || apiResponse?.total || 0, 
        [apiResponse]
    )

    // Memoize callbacks to prevent re-creating on every render
    const handleEdit = useCallback((contact: Contact) => {
        setSelectedContact(contact)
        setShowEditDialog(true)
    }, [])

    const handleDelete = useCallback((contact: Contact) => {
        setSelectedContact(contact)
        setShowDeleteDialog(true)
    }, [])

    const handleView = useCallback((contact: Contact) => {
        setSelectedContact(contact)
        setShowViewDialog(true)
    }, [])

    const handleDeleteConfirm = useCallback(() => {
        if (selectedContact) {
            deleteContact.mutate(selectedContact.id, {
                onSuccess: () => {
                    setShowDeleteDialog(false)
                    setSelectedContact(null)
                },
            })
        }
    }, [selectedContact, deleteContact])

    const handleExport = useCallback(() => {
        // TODO: Implement CSV export
        console.log('Export contacts to CSV')
    }, [])

    // Memoize columns to prevent re-creation
    const columns = useMemo(
        () => createContactColumns(canUpdate ? handleEdit : undefined, canDelete ? handleDelete : undefined, handleView, t),
        [handleEdit, handleDelete, handleView, canUpdate, canDelete, t]
    )

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">{t('contacts.loadingContacts')}</div>
            </div>
        )
    }

    return (
        <div className="space-y-[var(--spacing-section)]" style={{ padding: 'var(--spacing-page)' }}>
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl lg:text-2xl font-bold">{t('contacts.title')}</h1>
                    <p className="text-muted-foreground">
                        {t('contacts.description')}
                    </p>
                </div>
                {canCreate && (
                    <div className="flex space-x-2">
                        <Button onClick={() => setShowCreateDialog(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            {t('contacts.createContact')}
                        </Button>
                    </div>
                )}
            </div>

            {/* Stats */}
            <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-lg border bg-card p-6 md:col-start-2">
                    <div className="flex items-center space-x-2">
                        <Phone className="h-5 w-5 text-muted-foreground" />
                        <div>
                            <p className="text-sm font-medium">{t('contacts.totalContacts')}</p>
                            <p className="text-2xl font-bold">{totalContacts}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table - Full Width - Extended */}
            <div 
                className="overflow-x-auto" 
                style={{ 
                    marginLeft: 'calc(var(--spacing-page) * -2)',
                    marginRight: 'calc(var(--spacing-page) * -2)',
                    paddingLeft: 'calc(var(--spacing-page) * 0.05)',
                    paddingRight: 0,
                    width: 'calc(100vw - var(--sidebar-width-expanded) - var(--spacing-page) * 0.05)',
                    maxWidth: 'none'
                }}
            >
                <DataTable
                    columns={columns}
                    data={contacts}
                    searchKey="firstName"
                    searchPlaceholder={t('contacts.searchPlaceholder')}
                    searchValue={search}
                    onSearchChange={setSearch}
                    onExport={handleExport}
                    exportButtonText={t('contacts.export')}
                    enableRowSelection={false}
                    pagination={{
                        page,
                        limit,
                        total: totalContacts,
                        onPageChange: setPage,
                        onPageSizeChange: setLimit,
                    }}
                />
            </div>

            {/* Dialogs */}
            <ContactFormDialog
                open={showCreateDialog}
                onOpenChange={setShowCreateDialog}
                contact={null}
            />

            <ContactFormDialog
                open={showEditDialog}
                onOpenChange={setShowEditDialog}
                contact={selectedContact}
            />

            <ContactDeleteDialog
                open={showDeleteDialog}
                onOpenChange={setShowDeleteDialog}
                contact={selectedContact}
                onConfirm={handleDeleteConfirm}
                isLoading={deleteContact.isPending}
            />

            <ContactViewDialog
                open={showViewDialog}
                onOpenChange={setShowViewDialog}
                contact={selectedContact}
            />
        </div>
    )
}
